import { useState, useMemo, useCallback } from "react";
import { ROOM_FILTER_TYPES, VENDOR_KEYS } from "../constants/roomConstants";

/**
 * Optimized room filtering hook with pagination and debouncing
 * @param {Array} rooms - Array of room objects
 * @param {number} pageSize - Number of rooms per page
 * @returns {Object} - Filtered rooms and filter controls
 */
export const useOptimizedRoomFiltering = (rooms, pageSize = 100) => {
  const [selectionOption, setSelectionOption] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  // Pre-computed filter groups for faster filtering
  const filterGroups = useMemo(() => {
    if (!rooms || rooms.length === 0) {
      return {
        all: [],
        available: [],
        down: [],
        vendorNeeded: [],
        turnable: [],
        occupied: [],
        vacant: [],
      };
    }

    const groups = {
      all: rooms,
      available: [],
      down: [],
      vendorNeeded: [],
      turnable: [],
      occupied: [],
      vacant: [],
    };

    // Single pass through all rooms to categorize
    rooms.forEach((room) => {
      const status = room.status?.toLowerCase();

      // Occupied/Vacant classification
      if (status === "occupied") {
        groups.occupied.push(room);
      } else {
        groups.vacant.push(room);
      }

      // Existing classifications
      if (status === "up") {
        groups.available.push(room);
      } else if (status === "down") {
        groups.down.push(room);

        // Check if vendor needed
        const hasVendorNeeds = VENDOR_KEYS.some(
          (key) => room[key] && room[key] > 0
        );
        if (hasVendorNeeds) {
          groups.vendorNeeded.push(room);
        } else {
          groups.turnable.push(room);
        }
      }
    });

    return groups;
  }, [rooms]);

  // Get base filtered rooms without search
  const baseFilteredRooms = useMemo(() => {
    const option = selectionOption.toLowerCase();

    switch (option) {
      case ROOM_FILTER_TYPES.ALL:
        return filterGroups.all;
      case ROOM_FILTER_TYPES.AVAILABLE:
        return filterGroups.available;
      case ROOM_FILTER_TYPES.DOWN:
        return filterGroups.down;
      case ROOM_FILTER_TYPES.VENDOR_NEEDED:
        return filterGroups.vendorNeeded;
      case ROOM_FILTER_TYPES.TURNABLE:
        return filterGroups.turnable;
      case ROOM_FILTER_TYPES.OCCUPIED:
        return filterGroups.occupied;
      case ROOM_FILTER_TYPES.VACANT:
        return filterGroups.vacant;
      default:
        return filterGroups.all;
    }
  }, [filterGroups, selectionOption]);

  // Apply search filter with debouncing effect
  const searchFilteredRooms = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) {
      return baseFilteredRooms;
    }

    const searchLower = searchTerm.toLowerCase();
    return baseFilteredRooms.filter((room) =>
      room.displayName?.toLowerCase().includes(searchLower)
    );
  }, [baseFilteredRooms, searchTerm]);

  // Paginated results
  const paginatedRooms = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return searchFilteredRooms.slice(startIndex, endIndex);
  }, [searchFilteredRooms, currentPage, pageSize]);

  // Pagination info
  const paginationInfo = useMemo(
    () => ({
      totalRooms: searchFilteredRooms.length,
      totalPages: Math.ceil(searchFilteredRooms.length / pageSize),
      currentPage,
      pageSize,
      startIndex: currentPage * pageSize,
      endIndex: Math.min(
        (currentPage + 1) * pageSize,
        searchFilteredRooms.length
      ),
    }),
    [searchFilteredRooms.length, currentPage, pageSize]
  );

  // Optimized callbacks
  const handleFilterChange = useCallback((newFilter) => {
    setSelectionOption(newFilter);
    setCurrentPage(0); // Reset to first page
  }, []);

  const handleSearchChange = useCallback((newSearch) => {
    setSearchTerm(newSearch);
    setCurrentPage(0); // Reset to first page
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const goToNextPage = useCallback(() => {
    if (currentPage < paginationInfo.totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, paginationInfo.totalPages]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  return {
    // Filtered data
    filteredRooms: paginatedRooms,
    allFilteredRooms: searchFilteredRooms, // For operations that need all data

    // Filter state
    selectionOption,
    searchTerm,

    // Filter controls
    handleFilterChange,
    handleSearchChange,

    // Pagination
    paginationInfo,
    currentPage,
    handlePageChange,
    goToNextPage,
    goToPrevPage,

    // Performance metrics
    filterGroups: Object.fromEntries(
      Object.entries(filterGroups).map(([key, value]) => [key, value.length])
    ),
  };
};
